import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, FormControlLabel,
  Switch, Typography, Divider, CircularProgress, Alert,
  InputAdornment, Box
} from '@mui/material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/espacios';

const TIPOS_ESPACIO = ['Valla', 'Puente', 'Señal Vial', 'Digital', 'Mural', 'Paleta', 'Otro'];
const ESTADOS = ['Disponible', 'Ocupada', 'Reservada', 'En Mantenimiento'];
const NIVELES_SE = ['Alto', 'Medio', 'Bajo'];

const EMPTY_FORM = {
  Codigo: '',
  Tipo_Espacio: '',
  Direccion: '',
  Tamano: '',
  Provincia: '',
  Zona: '',
  Latitud: '',
  Longitud: '',
  Estado: 'Disponible',
  url_foto: '',
  Ancho: '',
  Alto: '',
  Orientacion: '',
  Iluminacion: false,
  HorarioIluminacion: '',
  FlujoDiario: '',
  NivelSocioeconomico: '',
  PuntosInteres: '',
  PrecioBase: '',
};

export default function BillboardFormModal({ open, onClose, onSaved, initialData }) {
  const isEdit = Boolean(initialData?.Codigo);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (open) {
      if (initialData) {
        // Normalise boolean/numeric values from DB
        setForm({
          ...EMPTY_FORM,
          ...initialData,
          Iluminacion: Boolean(initialData.Iluminacion),
          Latitud: initialData.Latitud ?? '',
          Longitud: initialData.Longitud ?? '',
          Ancho: initialData.Ancho ?? '',
          Alto: initialData.Alto ?? '',
          FlujoDiario: initialData.FlujoDiario ?? '',
          PrecioBase: initialData.PrecioBase ?? '',
          url_foto: '',
        });
        setPhotoPreview(initialData.url_foto || null);
      } else {
        setForm(EMPTY_FORM);
        setPhotoPreview(null);
      }
      setPhotoFile(null);
      setError('');
    }
  }, [open, initialData]);

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.Codigo.trim()) { setError('El Código es obligatorio.'); return; }
    setSaving(true);
    setError('');
    try {
      // Clean payload: remove empty strings → null/undefined
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === '' || v === null || v === undefined) continue;
        if (k === 'url_foto') continue; // No we don't send it to DB directly
        payload[k] = v;
      }

      if (isEdit) {
        const { Codigo, ...patchBody } = payload;
        await axios.patch(`${API_BASE_URL}/${form.Codigo}`, patchBody);
      } else {
        await axios.post(API_BASE_URL, payload);
      }

      if (photoFile) {
        const formData = new FormData();
        formData.append('file', photoFile);
        await axios.post(`${API_BASE_URL}/${form.Codigo}/foto`, formData, {
           headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const field = (label, fieldName, type = 'text', extra = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      type={type}
      value={form[fieldName]}
      onChange={set(fieldName)}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      {...extra}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
        {isEdit ? `✏️  Editar Valla – ${form.Codigo}` : '➕  Nueva Valla'}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* ── Identificación ── */}
        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Identificación
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box>
            {field('Código *', 'Codigo', 'text', { disabled: isEdit, helperText: isEdit ? 'No editable' : '' })}
          </Box>
          <Box>
            <TextField label="Estado" size="small" fullWidth select value={form.Estado} onChange={set('Estado')}>
              {ESTADOS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField label="Tipo de Espacio" size="small" fullWidth select value={form.Tipo_Espacio} onChange={set('Tipo_Espacio')}>
              {TIPOS_ESPACIO.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Box>
          <Box>{field('Tamaño (ej: 12x4)', 'Tamano')}</Box>
          <Box>{field('Dirección', 'Direccion')}</Box>
          <Box>{field('Provincia', 'Provincia')}</Box>
          <Box>{field('Zona', 'Zona')}</Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* ── Foto ── */}
        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Foto Principal
        </Typography>

        {/* Foto actual (solo en modo edición) */}
        {isEdit && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Foto actual:
            </Typography>
            {initialData?.url_foto ? (
              <Box
                component="img"
                src={initialData.url_foto}
                alt="Foto actual de la valla"
                sx={{
                  width: '100%',
                  maxHeight: 200,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'block',
                }}
              />
            ) : (
              <Box sx={{
                width: '100%', height: 80, display: 'flex', alignItems: 'center',
                justifyContent: 'center', borderRadius: 1, border: '1px dashed',
                borderColor: 'divider', color: 'text.disabled', fontSize: 14,
              }}>
                Sin foto registrada
              </Box>
            )}
          </Box>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={photoFile ? 6 : 12}>
            <Button variant="outlined" component="label" fullWidth>
              {isEdit ? (initialData?.url_foto ? '🔄 Reemplazar Foto' : '📷 Subir Foto') : '📷 Subir Foto'}
              <input type="file" accept="image/*" hidden onChange={(e) => {
                if (e.target.files[0]) {
                  setPhotoFile(e.target.files[0]);
                  setPhotoPreview(URL.createObjectURL(e.target.files[0]));
                }
              }} />
            </Button>
            {photoFile && <Typography variant="caption" display="block" sx={{ mt: 1 }}>Archivo: {photoFile.name}</Typography>}
          </Grid>
          {photoFile && (
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5 }}>
                ✅ Nueva foto seleccionada:
              </Typography>
              <Box
                component="img"
                src={photoPreview}
                alt="Vista previa de la nueva foto"
                sx={{
                  width: '100%',
                  maxHeight: 160,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: 'success.light',
                  display: 'block',
                }}
              />
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* ── Geolocalización ── */}
        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Geolocalización
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>{field('Latitud', 'Latitud', 'number', { inputProps: { step: 'any' } })}</Grid>
          <Grid item xs={6}>{field('Longitud', 'Longitud', 'number', { inputProps: { step: 'any' } })}</Grid>
          {form.Latitud && form.Longitud && (
            <Grid item xs={12}>
              <Button
                size="small" variant="outlined" color="secondary"
                href={`https://www.google.com/maps?q=${form.Latitud},${form.Longitud}`}
                target="_blank" rel="noopener noreferrer"
              >
                📍 Ver ubicación en Google Maps
              </Button>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* ── Atributos físicos ── */}
        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Atributos Físicos
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            {field('Ancho (m)', 'Ancho', 'number', { inputProps: { step: '0.01' } })}
          </Grid>
          <Grid item xs={6} sm={3}>
            {field('Alto (m)', 'Alto', 'number', { inputProps: { step: '0.01' } })}
          </Grid>
          <Grid item xs={12} sm={6}>{field('Orientación', 'Orientacion', 'text', { placeholder: 'Ej: Vista Norte, tráfico hacia el Este' })}</Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={Boolean(form.Iluminacion)} onChange={(e) => setForm(p => ({ ...p, Iluminacion: e.target.checked }))} />}
              label="¿Iluminada?"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            {field('Horario de Iluminación', 'HorarioIluminacion', 'text', { disabled: !form.Iluminacion, placeholder: 'Ej: 6pm – medianoche' })}
          </Grid>
        </Grid>

        <Divider sx={{ my: 1.5 }} />

        {/* ── Datos comerciales ── */}
        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Datos Comerciales y de Impacto
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box>
            {field('Precio Base mensual', 'PrecioBase', 'number', {
              InputProps: { startAdornment: <InputAdornment position="start">RD$</InputAdornment> },
              inputProps: { step: '0.01' }
            })}
          </Box>
          <Box>
            {field('Flujo Diario (vehículos)', 'FlujoDiario', 'number')}
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField label="Nivel Socioeconómico" size="small" fullWidth select value={form.NivelSocioeconomico} onChange={set('NivelSocioeconomico')}>
              <MenuItem value="">-- Seleccionar --</MenuItem>
              {NIVELES_SE.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            {field('Puntos de Interés cercanos', 'PuntosInteres', 'text', { placeholder: 'Ej: Universidad, Semáforo mayor, Plaza comercial', multiline: true, rows: 2 })}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Guardando...' : isEdit ? 'Guardar cambios' : 'Crear valla'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
